import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import * as actionTypes from "../actions/types.js";
import * as feedActionTypes from "./actionTypes.ts";
import { feedService } from "../../services/feedService.ts";
import type {
  FeedExploreFeedState,
  FeedPost,
  FeedFilterOption,
  FeedPortfolio,
} from "../../types/feed.ts";
import type { NavigateFunction } from "react-router-dom";
import { openEdifySpinner, closeMainModal } from "../actions/profileActions.js";
import { openMainModalDashboard } from "../actions/dashboardActions.js";
import { setFeedItemOrder } from "./actions.ts";
import { computeItemOrder } from "./selectors.ts";
import { fireAnalytics } from "../../utils/firebase.js";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { normalizePostLikes } from "../../utils/normalizePostLikes.ts";
import { pickPostCommentsFromApi } from "../../utils/pickPostCommentsFromApi.ts";

type FeedState = {
  feedView?: { selectedOption: string };
  posteos?: FeedPost[];
  ownPosteos?: FeedPost[];
  exploreFeed?: FeedExploreFeedState;
  allPortfolios?: FeedPortfolio[];
  userPortfolios?: FeedPortfolio[];
};

function findFeedPostInState(
  feed: FeedState | undefined,
  postIdNum: number,
  rawId: number | string
): FeedPost | undefined {
  const match = (p: FeedPost) => p.id === postIdNum || String(p.id) === String(rawId);
  const exploreItems = feed?.exploreFeed?.items;
  let fromExplore: FeedPost | undefined;
  if (exploreItems) {
    for (const item of exploreItems) {
      if (item.itemType === "post" && match(item)) {
        fromExplore = item;
        break;
      }
    }
  }
  return feed?.posteos?.find(match) ?? feed?.ownPosteos?.find(match) ?? fromExplore;
}
type GeneralState = { userData?: { id?: number | string } };
type RootState = { feed?: FeedState; general?: GeneralState };

type AppDispatch = ThunkDispatch<RootState, void, AnyAction>;

type FetchFeedOptions = { silent?: boolean };

function mapFeedPostFromApiList(p: FeedPost): FeedPost {
  const raw = p as FeedPost & { likedByMe?: boolean };
  const comments = pickPostCommentsFromApi(raw);
  const commentsCount =
    typeof p.comments_count === "number"
      ? p.comments_count
      : typeof p.commentsCount === "number"
        ? p.commentsCount
        : comments.length;
  return {
    ...raw,
    comments,
    commentsCount,
    likes: normalizePostLikes(raw.likes),
    likedByCurrentUser:
      raw.likedByCurrentUser !== undefined
        ? raw.likedByCurrentUser
        : raw.likedByMe,
  } as FeedPost;
}

export function feedFilterToApiView(option: FeedFilterOption): string {
  switch (option) {
    case "Mostrar":
      return "mostrar";
    case "recientes":
      return "recientes";
    case "recomendados":
      return "recomendados";
    case "productos":
      return "productos";
    case "servicios":
      return "servicios";
    case "posteos":
      return "posteos";
    case "propios":
      return "propios";
    default:
      return "mostrar";
  }
}

export type FetchExploreFeedOptions = {
  view: string;
  cursor?: string | null;
  append?: boolean;
  silent?: boolean;
  limit?: number;
};

export const fetchExploreFeed =
  (options: FetchExploreFeedOptions) =>
  (dispatch: AppDispatch) => {
    const { view, cursor, append = false, silent = false, limit = 20 } = options;
    const showGlobalLoading = !silent && !append;
    if (showGlobalLoading) dispatch({ type: actionTypes.START_LOADING });
    if (!append) {
      dispatch({ type: feedActionTypes.FEED_EXPLORE_RESET });
    }
    const endLoading = () => {
      if (showGlobalLoading) dispatch({ type: actionTypes.END_LOADING });
    };
    return feedService
      .getFeedExplore({
        view,
        limit,
        cursor: cursor ?? undefined,
      })
      .then((result) => {
        dispatch({
          type: feedActionTypes.FEED_EXPLORE_SET,
          payload: {
            items: result.items,
            nextCursor: result.nextCursor,
            hasMore: result.hasMore,
            append,
          },
        });
        return result;
      })
      .catch((err) => {
        console.log(err);
        return undefined;
      })
      .finally(() => {
        endLoading();
      });
  };

export const fetchPosteosWithComments =
  (order: string, type: string, options?: FetchFeedOptions) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void | unknown> => {
    const silent = options?.silent === true;
    if (!silent) dispatch({ type: actionTypes.START_LOADING });
    const endLoading = () => {
      if (!silent) dispatch({ type: actionTypes.END_LOADING });
    };
    return feedService
      .getPosteos(order, type)
      .then((posts) => {
        const postsArray = Array.isArray(posts) ? posts : [];
        const postsWithCommentsFromList = (postsArray as FeedPost[]).map(
          mapFeedPostFromApiList
        );
        dispatch({ type: actionTypes.GET_POSTEOS, payload: postsWithCommentsFromList });
        const state = getState();
        const orderPayload = computeItemOrder(
          postsWithCommentsFromList,
          state.feed?.ownPosteos ?? [],
          (state.feed?.allPortfolios ?? []) as FeedPortfolio[],
          (state.feed?.feedView?.selectedOption ?? "Mostrar") as FeedFilterOption,
          state.general?.userData?.id
        );
        dispatch(setFeedItemOrder(orderPayload));
        endLoading();
      })
      .catch((err) => {
        console.log(err);
        endLoading();
      });
  };

export const fetchUserPortfolios =
  (userIdParam: number | string) =>
  (dispatch: AppDispatch): Promise<void | unknown> => {
    return feedService
      .getUserPortfolios(userIdParam)
      .then((portfolios) => {
        const portfoliosArray = Array.isArray(portfolios) ? portfolios : [];
        dispatch({
          type: actionTypes.GET_USER_PORTFOLIOS,
          payload: portfoliosArray,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

export const fetchOwnFeedContent =
  (userIdParam: number | string, options?: FetchFeedOptions) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void | unknown> => {
    const silent = options?.silent === true;
    if (!silent) dispatch({ type: actionTypes.START_LOADING });
    const endLoading = () => {
      if (!silent) dispatch({ type: actionTypes.END_LOADING });
    };
    return Promise.all([
      feedService.getOwnPosteos(userIdParam),
      feedService.getUserPortfolios(userIdParam),
    ])
      .then(([posts, portfolios]) => {
        const postsArray = Array.isArray(posts) ? posts : [];
        const portfoliosArray = Array.isArray(portfolios) ? portfolios : [];
        const postsWithCommentsFromList = (postsArray as FeedPost[]).map(
          mapFeedPostFromApiList
        );
        dispatch({
          type: actionTypes.GET_OWN_POSTEOS,
          payload: postsWithCommentsFromList,
        });
        dispatch({
          type: actionTypes.GET_USER_PORTFOLIOS,
          payload: portfoliosArray,
        });
        const state = getState();
        const orderPayload = computeItemOrder(
          state.feed?.posteos ?? [],
          postsWithCommentsFromList,
          (state.feed?.allPortfolios ?? []) as FeedPortfolio[],
          "propios",
          state.general?.userData?.id,
          portfoliosArray as FeedPortfolio[]
        );
        dispatch(setFeedItemOrder(orderPayload));
        endLoading();
      })
      .catch((err) => {
        console.log(err);
        endLoading();
      });
  };

export const refetchFeedCurrentView =
  (silent?: boolean) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const feedView = getState().feed?.feedView;
    if (!feedView) return;
    const view = feedFilterToApiView(feedView.selectedOption as FeedFilterOption);
    void dispatch(
      fetchExploreFeed({
        view,
        append: false,
        silent: silent === true,
      })
    );
  };

export const getPosteoById =
  (id: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { post, comments } = await feedService.getPostWithComments(id);
      dispatch({
        type: actionTypes.GET_POSTEO_WITH_COMMENTS,
        payload: {
          post: mapFeedPostFromApiList(post as FeedPost),
          comments,
        },
      });
    } catch (err) {
      console.log(err);
    }
  };

type CreatePostBody = { images?: File | string; video?: File | string; content?: string };

export const createProfilePost =
  (body: CreatePostBody) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<unknown> => {
    const { images, video, content } = body;
    return feedService
      .createPost({ content, video, images })
      .then((data) => {
        dispatch({ type: actionTypes.RECENT_POST, payload: data });
        dispatch({ type: feedActionTypes.FEED_PREPEND_POST, payload: data });
        dispatch(refetchFeedCurrentView(true));
        const userData = getState().general?.userData;
        fireAnalytics("post_created", {
          edify_user_id: userData?.id ?? "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "publishing",
          action: "post_created",
          post_id: data?.id ?? "",
          post_type: "profile_post",
          has_images: !!images,
          has_video: !!video,
        });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };

export const createPost =
  (body: CreatePostBody, navigate?: NavigateFunction) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { images, video, content } = body;
    dispatch(openEdifySpinner(true));
    feedService
      .createPost({ content, video, images })
      .then((data) => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu posteo se publicó en Explorar posteos, servicios y productos.",
            iconType: "success",
            isSuccess: true,
            noLabel: "Ver en Explorar",
            noActionNeeded: false,
            customCloseMainModal: () => {
              dispatch(closeMainModal());
              navigate?.("/marketplace/explorar");
            },
          })
        );
        dispatch({ type: actionTypes.RECENT_POST, payload: data });
        dispatch({ type: feedActionTypes.FEED_PREPEND_POST, payload: data });
        dispatch(refetchFeedCurrentView(true));
        const userData = getState().general?.userData;
        fireAnalytics("post_created", {
          edify_user_id: userData?.id ?? "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "publishing",
          action: "post_created",
          post_id: data?.id ?? "",
          post_type: "feed_post",
          has_images: !!images,
          has_video: !!video,
        });
      })
      .catch(() => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            iconType: "error",
            body: "Tu posteo no se ha podido crear, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      });
  };

export const deletePost =
  (id: number) =>
  (dispatch: AppDispatch): void => {
    feedService
      .deletePost(id)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu posteo se ha eliminado con éxito",
            isSuccess: true,
            confirmHandler: null,
          })
        );
        dispatch(refetchFeedCurrentView());
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu posteo no se ha podido eliminar, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: false,
            confirmHandler: null,
          })
        );
      });
  };

export const likePost =
  (id: number | string) =>
  (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const postIdNum = Number(id);
    const state = getState();
    const userId = state.general?.userData?.id;
    const post = findFeedPostInState(state.feed, postIdNum, id);
    const currentLikes = post?.likes ?? [];
    const previousLikes = [...currentLikes];
    const previousLikedByCurrentUser = post?.likedByCurrentUser;
    const inLikesList = previousLikes.some(
      (l) => l?.user?.id != null && String(l.user.id) === String(userId)
    );
    const isLiked = inLikesList || Boolean(post?.likedByCurrentUser);
    dispatch({
      type: feedActionTypes.FEED_OPTIMISTIC_LIKE_POST,
      payload: { postId: postIdNum, userId, add: !isLiked },
    });
    const add = !isLiked;
    return feedService
      .likePost(postIdNum)
      .then((res) => {
        const likesRaw = res?.likes != null && Array.isArray(res.likes) ? res.likes : null;
        const likes = likesRaw != null ? normalizePostLikes(likesRaw) : null;
        const userInLikes =
          likes != null &&
          likes.length > 0 &&
          userId != null &&
          likes.some((l) => l?.user?.id != null && String(l.user.id) === String(userId));
        const confirmed = add ? userInLikes : !userInLikes;
        const likesToApply =
          likes != null && likes.length > 0 && confirmed ? likes : previousLikes;
        dispatch({
          type: feedActionTypes.FEED_MERGE_POST_PARTIAL,
          payload: { postId: postIdNum, likes: likesToApply, syncFromServer: true },
        });
      })
      .catch(() => {
        dispatch({
          type: feedActionTypes.FEED_MERGE_POST_PARTIAL,
          payload: {
            postId: postIdNum,
            likes: previousLikes,
            syncFromServer: false,
            useRollbackLikedBy: true,
            rollbackLikedByCurrentUser: previousLikedByCurrentUser,
          },
        });
      });
  };

type EditPosteoBody = {
  id: number | string;
  images?: string | File;
  video?: string | File;
  content?: string;
};

export const editPosteo =
  (body: EditPosteoBody) =>
  (dispatch: AppDispatch): void => {
    const { id, content, video, images } = body;
    dispatch(openEdifySpinner(true));
    feedService
      .updatePost({
        id: parseInt(String(id), 10),
        content,
        video,
        images,
      })
      .then((data) => {
        dispatch(openEdifySpinner(false));
        dispatch({ type: actionTypes.UPDATE_POSTEO_INFO, payload: { posteo: data } });
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            iconType: "success",
            body: "Tu posteo se ha editado con éxito",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      })
      .catch(() => {
        dispatch(openEdifySpinner(false));
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu posteo no se ha podido editar, por favor intenta nuevamente o escribenos a contacto@edify.la ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const createPostComment =
  (postId: number, content: string) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .createPostComment(postId, content)
      .then(() => {
        dispatch(getPosteoById(postId));
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario ha sido publicado en el muro",
            iconType: "success",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu comentario no se ha podido publicar, por favor intenta nuevamente o escribenos a contact@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: false,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const editPostComment =
  (commentId: number, content: string, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .editPostComment(commentId, content)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario se ha editado con éxito",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            iconType: "error",
            body: "Tu comentario no se ha podido editar, por favor intenta nuevamente o escribenos a contacto@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
          })
        );
      });
  };

export const likePostComment =
  (commentId: number, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .likePostComment(commentId)
      .then(() => dispatch(getPosteoById(postId)))
      .catch((err) => {
        console.log(err);
      });
  };

export const responsePostComment =
  (postId: number, commentId: number, content: string) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .responsePostComment(postId, commentId, content)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu respuesta ha sido publicada en el muro",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Ups!",
            body: "Tu respuesta no se ha podido publicar, por favor intenta nuevamente o escribenos a contacto@edify.la para que podamos ayudarte ¡Muchas gracias!",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };

export const deletePostComment =
  (commentId: number, postId: number) =>
  (dispatch: AppDispatch): Promise<unknown> => {
    return feedService
      .deletePostComment(commentId)
      .then(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Listo!",
            body: "Tu comentario se ha eliminado con éxito",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
          })
        );
        dispatch(getPosteoById(postId));
      })
      .catch(() => {
        dispatch(
          openMainModalDashboard({
            title: "¡Error!",
            body: "Tu comentario no se ha podido eliminar",
            isSuccess: true,
            confirmHandler: null,
            iconType: "error",
          })
        );
      });
  };
